import { useState } from 'react'
import { useWidth, useTip, sector, polar, spread, fitText, inkOn, TAU } from './kit.js'
import ChartTip from './ChartTip.jsx'

/**
 * Parts of a whole, drawn three ways, each with its labels on the picture itself: every part's
 * name, figure and share sits at the end of a thin leader line, and the big parts carry their
 * percentage inside them. On a narrow screen the labels move into a list underneath.
 *
 *   Donut  the classic ring, the total in the middle
 *   Arch   the same as a half ring, the total under the arch
 *   Rose   equal slices that reach out as far as their figure: each slice's AREA is its share, so a
 *          slice twice the figure is twice the colour, not four times
 *
 * `pieces` = [{name, value, color}], positive values only, in the product's own order.
 */
const pctOf = (v, total) => (total ? Math.round((v / total) * 100) : 0)
const MAX_CALLOUTS = 10

function useParts(pieces) {
  const total = pieces.reduce((a, p) => a + Number(p.value), 0)
  const order = pieces.map((p, i) => [p.value, i]).sort((a, b) => b[0] - a[0]).slice(0, MAX_CALLOUTS).map(x => x[1])
  return { total, labelled: new Set(order) }
}

/** Callouts on both sides of a round chart: a leader from the edge of each part out to its label. */
function Callouts({ items, cx, lo, hi, gapX }) {
  const right = items.filter(c => c.right), left = items.filter(c => !c.right)
  const place = (list) => {
    const ys = spread(list.map(c => c.ey), 32, lo, hi)
    return list.map((c, i) => ({ ...c, ly: ys[i] }))
  }
  return [...place(right), ...place(left)].map((c, i) => {
    const lx = c.right ? cx + gapX : cx - gapX
    return (
      <g key={i} pointerEvents="none">
        <polyline points={`${c.ax},${c.ay} ${c.ex},${c.ey} ${c.right ? lx - 6 : lx + 6},${c.ly}`} fill="none" stroke="#9CA3AF" strokeWidth="1" />
        <circle cx={c.ax} cy={c.ay} r="2" fill={c.color} />
        <text x={lx} y={c.ly - 1} textAnchor={c.right ? 'start' : 'end'} fontSize="12" fontWeight="700" fill="#1F2937">{c.name}</text>
        <text x={lx} y={c.ly + 13} textAnchor={c.right ? 'start' : 'end'} fontSize="11" fill="#6B7280">{c.sub}</text>
      </g>
    )
  })
}

function ListUnder({ pieces, total, fmt }) {
  return (
    <ul className="mt-3 space-y-1.5">
      {pieces.map((p, i) => (
        <li key={i} className="flex items-center gap-2 text-xs text-gray-700">
          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: p.color }} />
          <span className="flex-1 break-words">{p.name}</span>
          <span className="tabular-nums text-gray-900 font-semibold">{fmt(p.value)}</span>
          <span className="tabular-nums text-gray-400 w-10 text-right">{pctOf(p.value, total)}%</span>
        </li>
      ))}
    </ul>
  )
}

function partTip(tip, e, p, total, fmt) {
  tip.show(e, (
    <div>
      <p className="font-semibold text-gray-900 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />{p.name}</p>
      <p className="mt-1 text-gray-700"><span className="font-semibold tabular-nums">{fmt(p.value)}</span><span className="text-gray-500"> · {pctOf(p.value, total)}% of {fmt(total)}</span></p>
    </div>
  ))
}

/** Shared drawing for the full ring (span TAU from twelve o'clock) and the arch (span π from nine). */
function Ring({ pieces, fmt, half, percent }) {
  const [box, W] = useWidth()
  const tip = useTip()
  const [hover, setHover] = useState(null)
  const { total, labelled } = useParts(pieces)
  const top = pieces.reduce((m, p) => (p.value > m.value ? p : m), pieces[0])
  const wide = W >= 460
  const R = wide ? Math.max(70, Math.min(half ? 150 : 128, W / 2 - 165)) : Math.min(half ? 130 : 108, W / 2 - 16)
  const r0 = R * (half ? 0.56 : 0.6)
  const H = half ? R + (wide ? 64 : 40) : 2 * R + (wide ? 56 : 16)
  const cx = W / 2, cy = half ? R + (wide ? 34 : 12) : H / 2
  const start = half ? -Math.PI / 2 : 0
  const span = half ? Math.PI : TAU
  const pad = pieces.length > 1 ? 0.012 : 0

  let a = start
  const arcs = pieces.map((p, i) => {
    const a0 = a, a1 = a + (span * p.value) / total
    a = a1
    return { p, i, a0, a1, m: (a0 + a1) / 2 }
  })
  const callouts = wide ? arcs.filter(x => labelled.has(x.i)).map(x => {
    const [ax, ay] = polar(cx, cy, R + 3, x.m)
    const [ex, ey] = polar(cx, cy, R + 16, x.m)
    return { ax, ay, ex, ey, right: Math.sin(x.m) >= 0, color: x.p.color, name: fitText(x.p.name, 120, 12, true), sub: percent ? fmt(x.p.value) : `${fmt(x.p.value)} · ${pctOf(x.p.value, total)}%` }
  }) : []

  return (
    <div ref={box}>
      <div ref={tip.ref} className="relative" onMouseLeave={() => { tip.hide(); setHover(null) }}>
        {W > 0 && (
          <svg width={W} height={H} role="img" aria-label={`Parts of ${fmt(total)}`} className="pt-rise overflow-visible">
            {arcs.map(x => {
              const lit = hover === null || hover === x.i
              return (
                <path key={x.i} d={sector(cx, cy, r0, R, x.a0 + pad / 2, x.a1 - pad / 2)} fill={x.p.color} opacity={lit ? 1 : 0.35}
                      style={{ transition: 'opacity .2s', cursor: 'pointer', outline: 'none' }} tabIndex={0}
                      aria-label={`${x.p.name}: ${fmt(x.p.value)}, ${pctOf(x.p.value, total)}%`}
                      onMouseMove={e => { setHover(x.i); partTip(tip, e, x.p, total, fmt) }}
                      onFocus={e => { setHover(x.i); partTip(tip, e, x.p, total, fmt) }} onBlur={() => { setHover(null); tip.hide() }} />
              )
            })}
            {/* The big parts say their share inside themselves. */}
            {arcs.filter(x => x.a1 - x.a0 >= (half ? 0.32 : 0.45) && R - r0 >= 26).map(x => {
              const [tx, ty] = polar(cx, cy, (R + r0) / 2, x.m)
              return <text key={`p${x.i}`} x={tx} y={ty + 5} textAnchor="middle" fontSize="14" fontWeight="800" fill={inkOn(x.p.color)} pointerEvents="none">{pctOf(x.p.value, total)}%</text>
            })}
            {wide && <Callouts items={callouts} cx={cx} lo={14} hi={half ? cy : H - 18} gapX={R + 34} />}
            {/* The middle holds the whole — or, when the parts are already percentages (their whole is
                just 100%), the largest part. */}
            <text x={cx} y={half ? cy - 8 : cy + 4} textAnchor="middle" fontSize="24" fontWeight="800" fill="#111827">{percent ? fmt(top.value) : fmt(total)}</text>
            <text x={cx} y={half ? cy + 10 : cy + 22} textAnchor="middle" fontSize="11" fill="#6B7280">{percent ? fitText(top.name, r0 * 1.6, 11) : 'total'}</text>
          </svg>
        )}
        <ChartTip state={tip.state} />
      </div>
      {!wide && <ListUnder pieces={pieces} total={total} fmt={fmt} />}
    </div>
  )
}

/** `percent`: the figures are already shares of 100, so no second percentage is printed. */
export function Donut(props) { return <Ring {...props} half={false} /> }
export function Arch(props) { return <Ring {...props} half /> }

export function Rose({ pieces, fmt }) {
  const [box, W] = useWidth()
  const tip = useTip()
  const [hover, setHover] = useState(null)
  const { total, labelled } = useParts(pieces)
  const wide = W >= 460
  const R = wide ? Math.max(70, Math.min(140, W / 2 - 165)) : Math.min(120, W / 2 - 16)
  const r0 = R * 0.16
  const H = 2 * R + (wide ? 52 : 16)
  const cx = W / 2, cy = H / 2
  const max = Math.max(...pieces.map(p => p.value))
  const radius = (v) => Math.sqrt(r0 * r0 + (R * R - r0 * r0) * (v / max))   // area true to the figure
  const step = TAU / pieces.length
  const callouts = wide ? pieces.map((p, i) => ({ p, i })).filter(x => labelled.has(x.i)).map(({ p, i }) => {
    const m = step * (i + 0.5)
    const [ax, ay] = polar(cx, cy, radius(p.value) + 3, m)
    const [ex, ey] = polar(cx, cy, R + 14, m)
    return { ax, ay, ex, ey, right: Math.sin(m) >= 0, color: p.color, name: fitText(p.name, 120, 12, true), sub: `${fmt(p.value)} · ${pctOf(p.value, total)}%` }
  }) : []

  return (
    <div ref={box}>
      <div ref={tip.ref} className="relative" onMouseLeave={() => { tip.hide(); setHover(null) }}>
        {W > 0 && (
          <svg width={W} height={H} role="img" aria-label="Rose chart: each slice's area is its figure" className="pt-rise overflow-visible">
            {[0.25, 0.5, 0.75, 1].map(f => (
              <circle key={f} cx={cx} cy={cy} r={radius(max * f)} fill="none" stroke="#ECEBE6" strokeWidth="1" />
            ))}
            {pieces.map((p, i) => {
              const lit = hover === null || hover === i
              return (
                <path key={i} d={sector(cx, cy, r0, radius(p.value), step * i + 0.01, step * (i + 1) - 0.01)} fill={p.color} opacity={lit ? 1 : 0.35}
                      style={{ transition: 'opacity .2s', cursor: 'pointer', outline: 'none' }} tabIndex={0}
                      aria-label={`${p.name}: ${fmt(p.value)}`}
                      onMouseMove={e => { setHover(i); partTip(tip, e, p, total, fmt) }}
                      onFocus={e => { setHover(i); partTip(tip, e, p, total, fmt) }} onBlur={() => { setHover(null); tip.hide() }} />
              )
            })}
            {wide && <Callouts items={callouts} cx={cx} lo={14} hi={H - 18} gapX={R + 32} />}
          </svg>
        )}
        <ChartTip state={tip.state} />
      </div>
      {!wide && <ListUnder pieces={pieces} total={total} fmt={fmt} />}
    </div>
  )
}
